import { forwardRef } from "react";
import { SnackbarContent, useSnackbar } from "notistack";
import useEffectOnce from "hooks/useEffectOnce";
import ToasterService from "utils/toaster.util";
import "./Toaster.scss";

const CheckIcon = () => (
	<svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
		<path d="M5 12.5l4.5 4.5L19 7" />
	</svg>
);

const AlertIcon = () => (
	<svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
		<circle cx="12" cy="12" r="9" />
		<path d="M12 8v5M12 16.3h.01" />
	</svg>
);

const CloseIcon = () => (
	<svg className="ico" viewBox="0 0 24 24" aria-hidden="true">
		<path d="M6 6l12 12M18 6L6 18" />
	</svg>
);

type ToastVariant = "success" | "error";

const YpToast = forwardRef<
	HTMLDivElement,
	{ message: React.ReactNode; variant: ToastVariant; onDismiss: () => void }
>(({ message, variant, onDismiss }, ref) => (
	<SnackbarContent ref={ref} className={`yp-toast yp-toast--${variant}`}>
		<span className="yp-toast__icon" aria-hidden="true">
			{variant === "success" ? <CheckIcon /> : <AlertIcon />}
		</span>
		<span className="yp-toast__message">{message}</span>
		<button
			type="button"
			className="yp-toast__close"
			aria-label="Close"
			onClick={onDismiss}
		>
			<CloseIcon />
		</button>
	</SnackbarContent>
));

export default function Toaster() {
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();

	useEffectOnce(() => {
		const makeToast = (variant: ToastVariant) => (msg: string) => {
			if (!msg) return;
			enqueueSnackbar(msg, {
				variant,
				content: (key, message) => (
					<YpToast
						message={message}
						variant={variant}
						onDismiss={() => closeSnackbar(key)}
					/>
				),
			});
		};

		ToasterService.subscribe(makeToast("success"), makeToast("error"));
	});

	return null;
}
